<template>
  <main :aria-label="$t('general.all_programs')" class="program-list-page">
    <CompanyBreadcrumb :aria-label="$t('general.all_programs')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <template v-if="errorMessages && errorMessages.length">
      <div v-for="(error, index) in errorMessages"
           :key="index"
           class="card program-alert-card">
        <div class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p>
            <i class="uil-exclamation-triangle"></i> {{ error }}
          </p>
        </div>
      </div>
    </template>

    <div v-if="successMessage" class="card program-alert-card">
      <div class="uk-alert-success" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p>
          <i class="uil-check"></i> {{ successMessage }}
        </p>
      </div>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle program-page-header">
      <div class="section-header-left">
        <h2 class="program-page-title">
          <span>{{ $t('menu.company_dashboard.program') }}</span>
        </h2>
      </div>
      <div class="section-header-right">
        <button :disabled="!$hasPermission('create_program')"
                :style="{ cursor: !$hasPermission('create_program') ? 'not-allowed' : 'pointer' }"
                class="btn btn-default program-add-btn"
                type="button"
                @click="$router.push({ name: 'company.program.add' })">
          <i class="uil-plus"></i> {{ $t('general.add_program') }}
        </button>
      </div>
    </div>

    <div class="card program-list-card">
      <!-- Card header -->
      <div class="card-header actions-toolbar border-0 program-toolbar">
        <div class="d-flex justify-content-between program-toolbar-inner">
          <CompanySearchInput v-model="filter.search"
                              :placeholder="$t('general.search')"
                              class="program-toolbar-search"
                              @input="debouncedFilterSearch"
          />
          <div class="d-flex program-toolbar-actions">
            <sort-dropdown :sort-options="sortOptions" @sort-selected="onSortSelected"/>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div v-if="programs_loading" class="program-loading-wrap">
        <default-loading></default-loading>
      </div>
      <template v-else>
        <div class="sf-table sf-table--zebra sf-table--hover program-table-wrap">
          <table>
            <thead>
            <tr>
              <th scope="col">{{ $t('messages.title') }}</th>
              <th scope="col">{{ $t('general.description') }}</th>
              <th class="sf-table__center" scope="col">{{ $t('general.section_content') }}</th>
              <th scope="col">{{ $t('general.added_at') }}</th>
              <th class="sf-table__col-actions" scope="col"></th>
            </tr>
            </thead>
            <tbody class="list">
            <tr v-for="item in formattedItems" :key="item.id">
              <th class="program-col-name" scope="row">
                <div class="sf-table__media">
                  <router-link :to="{
                      name: 'company.programs.edit',
                      params: { id: item.id },
                    }" class="sf-table__name">
                    {{ item.title }}
                  </router-link>
                </div>
              </th>
              <td class="sf-table__truncate program-col-desc">{{ item.description }}</td>
              <td class="sf-table__center">
                {{ item.total_section }} / {{ item.total_content }}
              </td>
              <td>{{ item.created_at }}</td>
              <td class="sf-table__col-actions">
                <div class="sf-table__actions">
                  <router-link :to="{
                      name: 'company.programs.edit',
                      params: { id: item.id },
                    }" :uk-tooltip="$t('faq_categories.edit')" class="btn btn-icon btn-hover btn-sm btn-circle">
                    <i class="uil-edit"></i>
                  </router-link>
                  <a :uk-tooltip="$t('general.delete')"
                     class="btn btn-icon btn-hover btn-sm btn-circle"
                     href="javascript:void(0)"
                     @click="removeItem(item.id)">
                    <i class="uil-minus-circle text-danger"></i>
                  </a>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <pagination v-if="pagination && pagination.hasOwnProperty('links')"
                    :pagination="pagination"
                    class="mt-2 mb-3 program-pagination"
                    @changePage="setPage($event)"/>
      </template>
    </div>
  </main>
</template>

<script>
import store from "@/core/services";
import Pagination from "@/assets/components/Pagination";
import moment from "moment";
import debounce from "lodash/debounce";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import { confirmDialogDelete } from "@/utils/confirmDialog";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import CompanySearchInput from "@/view/company/components/search/CompanySearchInput.vue";
import SortDropdown from "@/view/components/dropdown/SortDropdown.vue";

import module, {
  BASE_URL,
  MODULE_NAME,
  ERROR,
  ITEMS,
  GET_ITEMS,
  DELETE_ITEM,
  LOADING,
} from "@/core/services/store/program.module";

export default {
  name: "ProgramList",
  components: { Pagination, DefaultLoading, CompanyBreadcrumb, CompanySearchInput, SortDropdown },
  data() {
    return {
      pagination: null,
      activeItem: null,
      successMessage: null,
      errorMessages: [],
      page: 1,
      per_page: 10,
      filter: {
        search: null,
        sort: "id",
        order: "desc",
      },
    };
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, module);
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t("general.programs") },
        { name: this.$t("general.all_programs") },
      ];
    },
    sortOptions() {
      return [
        { key: "title-asc", sort: "title", order: "asc", label: this.$t("sorting.a_z") },
        { key: "title-desc", sort: "title", order: "desc", label: this.$t("sorting.z_a") },
        { key: "created_at-asc", sort: "created_at", order: "asc", label: this.$t("sorting.created_at_asc") },
        { key: "created_at-desc", sort: "created_at", order: "desc", label: this.$t("sorting.created_at_desc") },
      ];
    },
    items: {
      get() {
        return store.getters[MODULE_NAME + "/" + ITEMS];
      },
      set(value) {
      },
    },
    programs_loading: {
      get() {
        return store.getters[MODULE_NAME + "/" + LOADING];
      },
      set(value) {
      },
    },
    error: {
      get() {
        return store.getters[MODULE_NAME + "/" + ERROR];
      },
      set(value) {
      },
    },
    formattedItems() {
      let results = [];
      if (this.items === null || !("data" in this.items)) return [];
      this.pagination = this.items;

      this.items.data.forEach((currentItem) => {
        results.push({
          id: currentItem.id,
          title: currentItem.title,
          description: currentItem.description ? currentItem.description : "-",
          total_section: currentItem.sections_count,
          total_content: currentItem.contents_count,
          created_at: currentItem.created_at
              ? moment(currentItem.created_at).format("DD/MM/YYYY HH:mm:ss")
              : "-",
        });
      });

      return results;
    },
  },
  methods: {
    getResult() {
      let self = this;
      this.$store.dispatch(MODULE_NAME + "/" + GET_ITEMS, {
        url: BASE_URL,
        filters: {
          like: self.filter.search,
          page: self.page,
          perPage: self.per_page,
          sort: self.filter.sort,
          order: self.filter.order,
        },
      });
    },
    async removeItem(id) {
      const isConfirmed = await confirmDialogDelete({ context: this });

      if (isConfirmed) {
        this.$store
            .dispatch(MODULE_NAME + "/" + DELETE_ITEM, {
              url: BASE_URL + "/" + id,
            })
            .then(() => {
              if (!this.error) {
                this.successMessage = this.$t("general.successfully_deleted");
                this.getResult();
              } else this.errorMessages.push(this.error);
            });
      }
    },
    onSortSelected({ sort, order }) {
      this.page = 1;
      this.filter.sort = sort;
      this.filter.order = order;
      this.getResult();
    },
    sortOrder(sort, order) {
      this.page = 1;
      this.filter.sort = sort;
      this.filter.order = order;
      this.getResult();
    },
    setPage(page) {
      this.page = page;
      this.getResult();
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
  },
  mounted() {
    this.getResult();
  },
  watch: {
    successMessage: function (val) {
      this.debouncedResetValues();
    },
    errorMessages: function (val) {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedFilterSearch = debounce(this.getResult, 500);
    this.debouncedResetValues = _.debounce(this.resetMessages, 2000);
  },
};
</script>

<style scoped>
.program-list-page {
  --program-primary: var(--primaryColor, #3e416d);
  --program-text: #1e293b;
  --program-muted: #64748b;
  --program-border: #e2e8f0;
  --program-soft: #f6f7fb;
  min-width: 0;
}

.program-alert-card {
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--program-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.program-list-page .uk-alert {
  margin: 0;
}

.program-list-page .uk-alert p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
}

.program-list-page .uk-alert-danger p {
  color: #dc3545;
}

.program-list-page .uk-alert-success p {
  color: #28a745;
}

.program-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.program-page-header .section-header-right > * {
  margin-left: 0;
}

.program-page-title {
  margin: 0;
  color: var(--program-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.program-add-btn {
  padding: 10px 17px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.program-add-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

.program-add-btn:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.program-list-card {
  overflow: hidden;
  margin-top: 0;
  border: 1px solid var(--program-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.program-toolbar {
  padding: 16px;
}

.program-toolbar-inner {
  align-items: center;
  gap: 16px;
}

.program-toolbar-search {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 480px;
}

.program-toolbar-inner ::v-deep .company-search-input,
.program-toolbar-inner ::v-deep .section-header-left {
  float: none;
  width: 100%;
  max-width: none;
  margin: 0;
}

.program-toolbar-inner ::v-deep .company-search-input input {
  height: auto;
  min-height: 0;
  padding: 10px 16px;
  font-size: 14px;
}

.program-toolbar-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.program-toolbar-actions ::v-deep .btn-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.program-toolbar-actions ::v-deep .btn-circle:active {
  transform: scale(0.98);
  opacity: 0.85;
}

.program-loading-wrap {
  padding: 32px 16px;
}

/* Tablo tasarimi .sf-table'dan gelir (assets/scss/_data-table.scss).
   Aciklama kolonunun 280px kirpmasi sistemin --sf-table-truncate
   varsayilaniyla ayni, bu yuzden desktop'ta ayrica tanim gerekmiyor. */

.program-pagination {
  padding: 0 8px 8px;
}

input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

@media (max-width: 959px) {
  .program-page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .program-page-header .section-header-left,
  .program-page-header .section-header-right {
    width: 100%;
  }

  .program-page-title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
  }

  .program-list-page .uk-alert p {
    font-size: 16px;
  }

  .program-add-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
  }

  .program-toolbar-inner ::v-deep .company-search-input input {
    height: 48px;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
  }

  .program-toolbar-actions ::v-deep .btn-circle {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
  }

  .program-toolbar-inner {
    flex-direction: column;
    align-items: stretch;
  }

  .program-toolbar-search {
    max-width: none;
    width: 100%;
  }

  .program-toolbar-actions {
    flex-wrap: wrap;
    width: 100%;
    justify-content: flex-end;
  }

  /* Tablo mobilde sikistirilmaz, yatay kaydirilir */
  .program-table-wrap {
    --sf-table-min-width: 880px;
    --sf-table-truncate: 240px;
  }

  .program-col-name {
    min-width: 200px;
  }

  .program-col-desc {
    min-width: 180px;
  }
}

@media (max-width: 767px) {
  .program-list-page {
    margin: 0;
    padding: 0 0 24px;
  }

  .program-page-title {
    font-size: 24px;
  }

  .program-list-card {
    border-radius: 14px;
  }

  .program-toolbar {
    padding: 16px;
  }

  .program-toolbar-actions {
    gap: 8px;
  }

  .program-pagination {
    padding: 0 16px 16px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .program-add-btn:hover:not(:disabled) {
    opacity: 0.92;
    cursor: pointer;
  }
}

@media (hover: none) {
  .program-list-page ::v-deep .btn-hover:hover {
    background-color: transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .program-add-btn:active:not(:disabled),
  .program-toolbar-actions ::v-deep .btn-circle:active {
    transform: none;
    opacity: 1;
  }
}
</style>
